import { useCallback, useMemo, useState } from 'react';
import { useNavigateWithParams } from '@hooks/useNavigateWithParams';
import { ImportJobRepresentation, ImportJobSource, ImportJobStatus } from '@common/api';
import { useFormatting } from '@providers/FormattingProvider';
import { pagePath } from '@pages/pagePaths';
import { PageContent, PageLayout, PageSheet } from '@components/page/PageLayout';
import { PAGE_ICONS } from '@util/folderItems';
import { useI18nContext } from '@i18n/i18n-react';
import { Button } from '@components/button';
import { useModal } from '@components/modal/ModalProvider';
import { SearchInput } from '@components/form';
import { ContextMenu } from '@components/context-menu/ContextMenu';
import { useDetectDimensions, useImportJobs, useUpdateImportJob } from '@data/importJob/importJobActions';
import { useGlobalLoadingContext } from '@features/loading/globalLoadingContext';
import { useFoldersActions } from '@data/folder/folderActions';
import { ImportJobStatusLabel, statusMessageMap } from './ImportJobStatusLabel';
import { DeleteImportJobModal } from './DeleteImportJobModal';
import { downloadFarseerFile } from './util';
import { Archive, Delete, Download, Refresh } from '@components/icons/FarseerIcons';
import { ImportFilter, INITIAL_FILTER } from './ImportJobFilter';
import { ImportJobLink } from './ImportJobs.style';
import { FlexRow } from '@components/layout/Flex';
import { CloneTooltipWrapper } from '@components/tooltip/Tooltip';
import { Labels } from '@features/labels/Labels';
import { useLabelActions } from './useImportJobLabelAction';

import { getFullName, getInitials } from '@data/user/user';
import { patch, Update } from '@use-gpu/state';
import { AvatarBadges } from '@components/avatar/AvatarBadges';
import {
    TableSheet,
    TableSheetColumnConfig,
    TableSheetColumnsConfig,
    TableSheetFilter,
    TableSheetIgnoreDragContainer,
    TableSheetMenuRenderProps,
    TableSheetRowsConfig,
    TableSheetUpdate,
    Rectangle,
    makeArrayDataType,
    makeRowAccessor,
    makeStringDataType,
    normalizeSelection,
    useTableSheetStateLocal,
} from '@components/table-sheet';
import { useUserOptions } from '@features/share-editor/hooks/useUserOptions';

export default function ImportJobsPage() {
    const { LL } = useI18nContext();
    const navigate = useNavigateWithParams();
    const { showModal } = useModal();
    const { formatTimestamp } = useFormatting();

    const { data: importJobs = [] } = useImportJobs();
    const updateImportJob = useUpdateImportJob();
    const detectDimensions = useDetectDimensions();
    const { refreshAllFolders } = useFoldersActions();

    const { globalLoadingWrapped } = useGlobalLoadingContext();
    const [filter, setFilter] = useState<ImportFilter>(INITIAL_FILTER);

    const [selectedJob, setSelectedJob] = useState<ImportJobRepresentation | null>(null);

    const usedLabels = useMemo(() => [...new Set(importJobs.flatMap((job) => job.labels || []))], [importJobs]);
    const labelFilterOptions = useMemo(() => usedLabels.map((label) => ({ label, value: label })), [usedLabels]);

    const statusFilterOptions = useMemo(
        () =>
            Object.values(ImportJobStatus).map((status) => ({
                label: LL.STATUS_MESSAGE_MAP[statusMessageMap[status]](),
                value: status,
            })),
        [LL]
    );

    const allStatuses = statusFilterOptions.map((o) => o.value);

    const { renameLabel, deleteLabel } = useLabelActions(selectedJob, importJobs);
    const { usersMap } = useUserOptions();

    const filteredJobs = useMemo(
        () =>
            importJobs.filter((job) => {
                const matchesSearch =
                    filter.nameSearch.length === 0 || job.title.toLowerCase().includes(filter.nameSearch.toLowerCase());
                const matchesStatus = filter.statuses.length === 0 || filter.statuses.includes(job.status);
                const matchesLabels =
                    filter.labelNames.length === 0 ||
                    filter.labelNames.some((name) =>
                        job.labels?.some((jobLabel) => jobLabel.toLowerCase() === name.toLowerCase())
                    );

                return matchesSearch && matchesStatus && matchesLabels;
            }),
        [importJobs, filter]
    );

    const handleDelete = (job: ImportJobRepresentation) => {
        showModal(
            <DeleteImportJobModal
                importJob={job}
                onDeleted={() => {
                    refreshAllFolders();
                }}
            />
        );
    };

    const handleArchive = async (jobId: number) => {
        await globalLoadingWrapped(() => updateImportJob.mutateAsync({ id: jobId, status: ImportJobStatus.ARCHIVED }));
    };

    const handleOpenJob = useCallback(
        async (importJob: ImportJobRepresentation) => {
            navigate(`${pagePath.Import}/job/${importJob.id}`);
            if (importJob.status === ImportJobStatus.CREATED || importJob.status === ImportJobStatus.REVERTED) {
                await detectDimensions.mutateAsync(importJob.id);
            }
        },
        [navigate, detectDimensions]
    );

    const handleJobChange = async (
        ops: TableSheetUpdate<ImportJobRepresentation, Update<ImportJobRepresentation>>[]
    ) => {
        for (const op of ops) {
            let newRow = op.row;
            for (const update of op.updates) newRow = patch(newRow, update);
            await updateImportJob.mutate(newRow);
        }
    };

    const columns = useMemo(() => {
        const columns: TableSheetColumnConfig<ImportJobRepresentation, Update<ImportJobRepresentation>>[] = [];

        columns.push(
            {
                key: 'name',
                header: {
                    label: LL.NAME(),
                    width: 340,
                },
                data: {
                    ...makeRowAccessor(),
                    readonly: () => true,
                },
                cell: {
                    render: ({ value: job }) => (
                        <CloneTooltipWrapper label={job.title} allowedPlacements={['top-start']}>
                            <ImportJobLink
                                style={{
                                    overflow: 'clip',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    minWidth: '100%',
                                }}
                                onClick={() => handleOpenJob(job)}
                            >
                                {job.title}
                            </ImportJobLink>
                        </CloneTooltipWrapper>
                    ),
                },
            },
            {
                key: 'labels',
                header: {
                    label: (
                        <TableSheetFilter
                            title={LL.LABELS()}
                            options={labelFilterOptions}
                            selectAll={labelFilterOptions.length === filter.labelNames.length}
                            values={filter.labelNames}
                            canSelectBlanks={false}
                            onChange={(labels) => {
                                setFilter({ ...filter, labelNames: labels.selectAll ? usedLabels : labels.values });
                            }}
                        />
                    ),
                    width: 250,
                },
                data: makeRowAccessor('labels'),
                type: makeArrayDataType(makeStringDataType()),
                cell: {
                    render: ({ value: labels, onChange }) => (
                        <FlexRow>
                            <TableSheetIgnoreDragContainer>
                                <Labels
                                    allLabels={usedLabels}
                                    colorStrategy="random"
                                    labels={labels}
                                    getLabelName={(label: string) => label}
                                    onRemove={(label) => onChange?.(labels.filter((name: string) => name !== label))}
                                    onAdd={(label) => onChange?.([...labels, label])}
                                    onUpdate={(oldLabel, newLabel) => renameLabel(oldLabel, newLabel)}
                                    onCreate={async (name) => name}
                                    onDelete={(label) => deleteLabel(label)}
                                />
                            </TableSheetIgnoreDragContainer>
                        </FlexRow>
                    ),
                },
            },
            {
                key: 'description',
                header: {
                    label: LL.DESCRIPTION(),
                    width: 200,
                },
                data: makeRowAccessor('description'),
                type: makeStringDataType(),
            },
            {
                key: 'importer',
                header: {
                    label: LL.IMPORTER(),
                    width: 200,
                },
                data: {
                    ...makeRowAccessor('creatorId'),
                    readonly: () => true,
                },
                cell: {
                    render: ({ value }) => {
                        const user = usersMap.get(value);
                        const data = [
                            { label: getInitials(user), hoverLabel: getFullName(user), image: user?.avatarUrl },
                        ];
                        return (
                            <FlexRow $width="100%" $height="100%" $align="center">
                                <AvatarBadges data={data} hasLabel />
                            </FlexRow>
                        );
                    },
                },
            },
            {
                key: 'date',
                header: {
                    label: LL.CREATION_DATE(),
                    width: 130,
                },
                data: {
                    ...makeRowAccessor('createdAt'),
                    readonly: () => true,
                },
                cell: {
                    render: ({ value: createdAt }) => formatTimestamp(createdAt, 'minutes'),
                },
            },
            {
                key: 'status',
                header: {
                    label: (
                        <TableSheetFilter
                            title={LL.STATUS()}
                            options={statusFilterOptions}
                            selectAll={statusFilterOptions.length === filter.statuses.length}
                            values={filter.statuses}
                            canSelectBlanks={false}
                            onChange={(statuses) => {
                                setFilter({ ...filter, statuses: statuses.selectAll ? allStatuses : statuses.values });
                            }}
                        />
                    ),
                    width: 85,
                },
                data: {
                    ...makeRowAccessor('status'),
                    readonly: () => true,
                },
                cell: {
                    render: ({ value: status }) => <ImportJobStatusLabel status={status} />,
                },
            }
        );

        return columns;
    }, [
        LL,
        allStatuses,
        deleteLabel,
        filter,
        formatTimestamp,
        labelFilterOptions,
        renameLabel,
        statusFilterOptions,
        usedLabels,
        handleOpenJob,
        usersMap,
    ]);

    // Rows and columns config
    const tableRows: TableSheetRowsConfig<ImportJobRepresentation> = useMemo(
        () => ({
            count: filteredJobs.length,
            key: (i: number) => filteredJobs[i]?.id ?? i,
            get: (i: number) => filteredJobs[i],
            size: () => 32,
        }),
        [filteredJobs]
    );

    const logRowCount = Math.max(0, Math.floor(Math.log10(filteredJobs.length)) - 1);
    const tableColumns: TableSheetColumnsConfig<ImportJobRepresentation, Update<ImportJobRepresentation>> = useMemo(
        () => ({
            count: columns.length,
            key: (i: number) => columns[i]?.key ?? i,
            get: (i: number) => columns[i],
            size: (i: number) => columns[i]?.header.width ?? 34 + logRowCount * 10,
        }),
        [columns, logRowCount]
    );

    const tableSheetState = useTableSheetStateLocal({
        rows: tableRows,
        columns: tableColumns,
        configKey: 'import-table',
        onSheetChange: handleJobChange,
        onSelectionChange: (selection: Rectangle | null) => {
            if (!selection) {
                setSelectedJob(null);
                return;
            }

            const { rowOrder } = tableSheetState;
            const [[, start], [, end]] = normalizeSelection(selection);

            const items = rowOrder
                .slice(start, end + 1)
                .map((rowIndex) => filteredJobs[rowIndex])
                .filter(Boolean);

            setSelectedJob(items[0] ?? null);
        },
    });

    const renderContextMenu = (props: TableSheetMenuRenderProps) => {
        const { selection, rowOrder } = tableSheetState;
        if (!selection) return null;

        const [[, start], [, end]] = normalizeSelection(selection);

        const items = rowOrder
            .slice(start, end + 1)
            .map((rowIndex) => filteredJobs[rowIndex])
            .filter(Boolean);

        if (items.length === 0) return null;

        const [selectedJob] = items;

        return (
            <ContextMenu {...props}>
                <ImportActionsMenu
                    selectedJob={selectedJob}
                    onArchive={handleArchive}
                    onDelete={handleDelete}
                    onDownload={(job) => {
                        if (job.farseerFileId) {
                            downloadFarseerFile(job.farseerFileId, job.title);
                        }
                    }}
                    onReplace={(job) => navigate(`${pagePath.Import}/replace/${job.id}`)}
                />
            </ContextMenu>
        );
    };

    return (
        <PageLayout
            title={LL.IMPORT()}
            actions={
                <Button $type="primary" title={LL.NEW_IMPORT()} onClick={() => navigate(`${pagePath.Import}/new`)} />
            }
            icon={PAGE_ICONS.Import}
        >
            <PageContent $padInline $padBlock style={{ flexGrow: 0 }}>
                <SearchInput
                    placeholder={LL.SEARCH_FOR_IMPORTS()}
                    value={filter.nameSearch}
                    onChange={(nameSearch) => setFilter((prevFilter) => ({ ...prevFilter, nameSearch }))}
                    width={300}
                />
            </PageContent>
            <PageSheet>
                <TableSheet
                    {...tableSheetState}
                    fill={[false, true]}
                    negative={[1, 1]}
                    sticky={[1, 0]}
                    minCellSize={[20, 32]}
                    onRowResize={undefined}
                    contextMenu={renderContextMenu}
                    //canLoadMore={canLoadMore}
                    //onLoadMore={onLoadMore}
                />
            </PageSheet>
        </PageLayout>
    );
}

interface ImportActionsMenuProps {
    selectedJob: ImportJobRepresentation | null;
    onArchive: (jobId: number) => void;
    onDelete: (job: ImportJobRepresentation) => void;
    onDownload: (job: ImportJobRepresentation) => void;
    onReplace: (job: ImportJobRepresentation) => void;
}

const ImportActionsMenu = ({ selectedJob, onArchive, onDelete, onDownload, onReplace }: ImportActionsMenuProps) => {
    const { LL } = useI18nContext();
    if (!selectedJob) return null;

    return (
        <>
            <ContextMenu.Item
                item={{
                    label: LL.ARCHIVE(),
                    icon: Archive,
                    disabled: selectedJob.status === ImportJobStatus.ARCHIVED,
                    action: () => onArchive(selectedJob.id),
                }}
            />
            <ContextMenu.Item
                item={{
                    label: LL.REPLACE(),
                    icon: Refresh,
                    disabled: selectedJob.status === ImportJobStatus.ARCHIVED,
                    action: () => onReplace(selectedJob),
                }}
            />
            <ContextMenu.Item
                item={{
                    label: LL.DOWNLOAD(),
                    icon: Download,
                    disabled: selectedJob.source !== ImportJobSource.FARSEER_FILE || !selectedJob.farseerFileId,
                    action: () => onDownload(selectedJob),
                }}
            />
            <ContextMenu.Divider />
            <ContextMenu.Item
                item={{
                    label: LL.DELETE(),
                    icon: Delete,
                    action: () => onDelete(selectedJob),
                }}
            />
        </>
    );
};
